import { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import { useOrganizationStore } from "../hooks";
import { getUnitTypeMeta } from "../utils/organization.helpers";
import OrgStatusTag from "./OrgStatusTag";

const LEVEL_COLORS = [
  "from-blue-500/90 to-blue-500/70 text-white",
  "from-amber-500/90 to-amber-500/70 text-white",
  "from-indigo-500/90 to-indigo-500/70 text-white",
  "from-green-500/90 to-green-500/70 text-white",
];

const LEVEL_COLORS_BUSINESS_TYPE = [
  "from-blue-500/90 to-blue-500/70 text-white",
  "from-emerald-500/90 to-emerald-500/70 text-white",
  "from-amber-500/90 to-amber-500/70 text-white",
  "from-indigo-500/90 to-indigo-500/70 text-white",
  "from-green-500/90 to-green-500/70 text-white",
];

const FIXED_LEVEL_COLORS = [
  "bg-blue-500/90 text-white",
  "bg-amber-500/90 text-white",
  "bg-indigo-500/90 text-white",
  "bg-green-500/90 text-white",
  "bg-emerald-500/90 text-white",
];

const FIXED_LEVEL_BUSINESSCOLORS = [
  "bg-blue-500/90 text-white",
  "bg-emerald-500/90 text-white",
  "bg-amber-500/90 text-white",
  "bg-indigo-500/90 text-white",
  "bg-green-500/90 text-white",
];

const LEVEL_REGEX = /^level_(\d+)$/;
const MIN_LEAF_WIDTH = 260;
const LINE = "bg-muted-foreground/40";

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const buildGroupedDiagramTree = (tree = []) => {
  if (!Array.isArray(tree) || tree.length === 0) return tree;

  return tree.map((root) => {
    const groupNodes = Array.isArray(root.children) ? root.children : [];

    const nextGroupNodes = groupNodes.map((group) => {
      const level1Entries = new Map();

      const registerLevel1 = (node) => {
        if (!node || level1Entries.has(node.id)) return;
        level1Entries.set(node.id, { node, listItems: [] });
      };

      const visit = (node, currentLevel1) => {
        if (!node) return;
        const level = getLevelNumber(node.unitLevel);

        if (level === 1) {
          currentLevel1 = node;
          registerLevel1(node);
        } else if (level && level >= 2 && currentLevel1) {
          const entry = level1Entries.get(currentLevel1.id);
          if (entry) entry.listItems.push(node);
        }

        if (Array.isArray(node.children) && node.children.length) {
          node.children.forEach((child) => visit(child, currentLevel1));
        }
      };

      (group.children || []).forEach((child) => visit(child, null));

      const level1Nodes = Array.from(level1Entries.values()).map(
        ({ node, listItems }) => ({
          ...node,
          children: [],
          listItems,
        }),
      );

      return {
        ...group,
        children: level1Nodes,
      };
    });

    return {
      ...root,
      children: nextGroupNodes,
    };
  });
};

const resolveLevelColors = (filter) => {
  if (filter && filter.startsWith("level_")) return FIXED_LEVEL_COLORS;
  return FIXED_LEVEL_BUSINESSCOLORS;
};
const getMaxDepth = (node) => {
  const children = Array.isArray(node.children) ? node.children : [];

  if (children.length === 0) return 1;

  return 1 + Math.max(...children.map(getMaxDepth));
};
const fillMissingLevels = (node, maxDepth, currentDepth = 1) => {
  const children = Array.isArray(node.children) ? node.children : [];

  // Reached max depth
  if (currentDepth >= maxDepth) {
    return {
      ...node,
      children: [],
    };
  }

  // Branch ended early → inject placeholder
  if (children.length === 0) {
    return {
      ...node,
      children: [
        fillMissingLevels(
          {
            id: `placeholder-${node.id}-${currentDepth}`,
            isPlaceholder: true,
            name: "",
            children: [],
          },
          maxDepth,
          currentDepth + 1,
        ),
      ],
    };
  }

  return {
    ...node,
    children: children.map((child) =>
      fillMissingLevels(child, maxDepth, currentDepth + 1),
    ),
  };
};
const enrichWithLeafCount = (node) => {
  if (!node) return node;
  const enriched = { ...node };
  if (!enriched.children?.length) {
    enriched._leafCount = 1;
    enriched.children = [];
  } else {
    enriched.children = enriched.children.map(enrichWithLeafCount);
    enriched._leafCount = enriched.children.reduce(
      (sum, child) => sum + child._leafCount,
      0,
    );
  }
  return enriched;
};

const buildLevels = (roots) => {
  const levels = [];
  let current = roots;
  while (current.length > 0) {
    levels.push(current);
    current = current.flatMap((n) => n.children || []);
  }
  return levels;
};

const getLegendLabel = ({ nodes, depth, groupMode, t }) => {
  const firstNode = Array.isArray(nodes) ? nodes.find(Boolean) : null;

  if (firstNode?.isGroup) {
    if (groupMode === "country") return t("Country");
    return t("Group");
  }

  if (firstNode?.unitLevel) {
    return t(getUnitTypeMeta(firstNode.unitLevel).label);
  }

  if (depth === 0) return t("Parent Company");
  return `${t("Level")} ${depth}`;
};

const NodeCard = ({ node, level, filter }) => {
  if (node.isPlaceholder) {
    return <div className="invisible h-[72px] w-full" />;
  }

  const { t } = useTran();
  const meta = getUnitTypeMeta(node.unitLevel);
  const colors = resolveLevelColors(filter);
  const colorClass = colors[level % colors.length];
  const locationLabel =
    node.locationLabel || node.countryLabel || node.country || node.regionLabel;
  const listItems = Array.isArray(node.listItems) ? node.listItems : [];
  const listIsScrollable = filter !== "level";
  const listContainerClass = `w-full rounded-radius border border-border bg-background/80 px-3 py-2 text-left text-xs text-muted-foreground shadow-sm ${
    listIsScrollable ? "max-h-32 overflow-y-auto pr-2" : ""
  }`;

  return (
    <div className="flex w-full flex-col items-center ">
      <div
        className={`w-full rounded-radius ${colorClass} px-3 py-2 text-center shadow-sm`}
      >
        <p className="truncate text-sm font-semibold">{node.name}</p>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px]">
          {/* <Badge */}
          {/*   variant="outline" */}
          {/*   className="border-white/30 bg-white/20 px-2 text-white shadow-sm" */}
          {/* > */}
          {/*   {t(meta.label)} */}
          {/* </Badge> */}
          {locationLabel ? (
            <Badge
              variant="outline"
              className="border-slate-100/50 bg-slate-900/30 px-2 py-0.5 text-slate-100 h-5"
            >
              {locationLabel}
            </Badge>
          ) : null}
          <OrgStatusTag
            status={node.status}
            inactiveDate={node.inactiveDate}
            className="px-2 rounded-2xl h-5 font-medium bg-slate-900/30 border-gray-400 dark:border-gray-500 text-amber-500"
          />
        </div>
      </div>

      {listItems.length > 0 ? (
        <div className={`${listContainerClass} mt-2`}>
          <ul className="space-y-1">
            {listItems.map((item) => (
              <li key={item.id || item.name} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                <span className="text-foreground">{item.name}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const ConnectorRow = ({ parentNodes, parentPath = [] }) => (
  <div className="flex">
    {parentNodes.map((parent, idx) => {
      const children = (parent.children || []).filter(
        (child) => !child.isPlaceholder,
      );

      const key = [...parentPath, parent.id || parent.name, idx].join("-");

      return (
        <div
          key={key}
          style={{ flex: `${parent._leafCount} 0 0` }}
          className="flex min-w-0 flex-col items-center"
        >
          {children.length > 0 ? (
            <>
              {/* Parent vertical connector */}
              <div className={`h-6 w-0.5 ${LINE}`} />

              {/* Single child */}
              {children.length === 1 ? (
                <div className={`h-[30px] w-0.5 ${LINE}`} />
              ) : (
                /* Multiple children */
                <div className="flex w-full">
                  {children.map((child, cidx) => {
                    const isFirst = cidx === 0;
                    const isLast = cidx === children.length - 1;
                    const childKey = [
                      ...parentPath,
                      parent.id || parent.name,
                      child.id || child.name,
                      cidx,
                    ].join("-");

                    return (
                      <div
                        key={childKey}
                        style={{ flex: `${child._leafCount} 0 0` }}
                        className="flex min-w-0 flex-col items-center"
                      >
                        {/* Horizontal connectors */}
                        {isFirst ? (
                          <div className="flex h-0.5 w-full">
                            <div className="w-1/2" />
                            <div className={`w-1/2 ${LINE}`} />
                          </div>
                        ) : isLast ? (
                          <div className="flex h-0.5 w-full">
                            <div className={`w-1/2 ${LINE}`} />
                            <div className="w-1/2" />
                          </div>
                        ) : (
                          <div className={`h-0.5 w-full ${LINE}`} />
                        )}

                        {/* Child vertical connector */}
                        <div className="relative flex justify-center">
                          <div className={`h-7 w-0.5 ${LINE}`} />

                          {/* Joint circle */}
                          <div
                            className={`absolute top-0 h-2 w-2 -translate-y-1/2 rounded-full border  bg-gray-400`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : null}
        </div>
      );
    })}
  </div>
);
const ChildNodeRow = ({ parentNodes, level, filter, parentPath = [] }) => (
  <div className="flex">
    {parentNodes.map((parent, pidx) => {
      const children = (parent.children || []).filter(
        (child) => !child.isPlaceholder,
      );
      return (
        <div
          key={[...parentPath, parent.id || parent.name, pidx].join("-")}
          style={{ flex: `${parent._leafCount} 0 0` }}
          className="flex min-w-0"
        >
          {children.map((child, cidx) => (
            <div
              key={[
                ...parentPath,
                parent.id || parent.name,
                child.id || child.name,
                cidx,
              ].join("-")}
              style={{ flex: `${child._leafCount} 0 0` }}
              className="flex min-w-0 justify-center px-1"
            >
              <NodeCard node={child} level={level} filter={filter} />
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

const LoadingState = () => (
  <div className="flex min-w-[180px] flex-col items-center gap-3">
    <div className="h-16 w-full animate-pulse rounded-radius bg-muted/70" />
    <div className="h-5 w-1/2 animate-pulse rounded-full bg-muted/60" />
  </div>
);

const EmptyState = () => {
  const { t } = useTran();
  return (
    <div className="flex h-32 w-full items-center justify-center rounded-radius border border-dashed border-border text-sm text-muted-foreground">
      {t(
        "No units to visualize yet. Add a unit to see the relationship diagram.",
      )}
    </div>
  );
};

const OrganizationDiagram = observer(({ tree: treeOverride, groupMode }) => {
  const { t } = useTran();
  const { tree: storeTree, loading } = useOrganizationStore();
  const tree = treeOverride ?? storeTree;

  const diagramTree = tree;
  // if (groupMode === "businessType") {
  //   diagramTree = buildGroupedDiagramTree(tree);
  // } else {
  //   diagramTree = tree;
  // }

  const enrichedTree = useMemo(() => {
    const roots = diagramTree || [];

    const maxDepth = roots.length > 0 ? Math.max(...roots.map(getMaxDepth)) : 0;

    const normalizedTree = roots.map((root) =>
      fillMissingLevels(root, maxDepth),
    );

    return normalizedTree.map(enrichWithLeafCount);
  }, [diagramTree]);

  const levels = useMemo(() => buildLevels(enrichedTree), [enrichedTree]);

  const totalLeaves = useMemo(
    () => enrichedTree.reduce((sum, r) => sum + r._leafCount, 0),
    [enrichedTree],
  );
  const legendItems = useMemo(() => {
    if (levels.length === 0) return [];
    const colors = resolveLevelColors(groupMode);

    return levels.map((nodes, depth) => ({
      key: `legend-${depth}`,
      label: getLegendLabel({ nodes, depth, groupMode, t }),
      colorClass: colors[depth % colors.length],
    }));
  }, [groupMode, levels, t]);

  return (
    <div className="space-y-3">
      <div className="relative overflow-auto">
        <div
          style={{
            minWidth:
              totalLeaves > 0 ? totalLeaves * MIN_LEAF_WIDTH : undefined,
          }}
        >
          {loading ? (
            <div className="flex flex-wrap justify-center gap-6">
              {Array.from({ length: 3 }).map((_, idx) => (
                <LoadingState key={idx} />
              ))}
            </div>
          ) : null}

          {!loading && levels.length === 0 ? <EmptyState /> : null}

          {!loading && levels.length > 0 ? (
            <div className="flex flex-col items-stretch">
              {/* Root level */}
              <div className="flex">
                {levels[0].map((root) => (
                  <div
                    key={root.id || root.name}
                    style={{ flex: `${root._leafCount} 0 0` }}
                    className="flex min-w-0 justify-center px-1"
                  >
                    <NodeCard node={root} level={0} filter={groupMode} />
                  </div>
                ))}
              </div>

              {/* Subsequent levels: connector row + child node row */}
              {/* {levels.slice(1).map((_levelNodes, idx) => (
                  <div key={`level-${idx + 1}`}>
                    <ConnectorRow parentNodes={levels[idx]} />
                    <ChildNodeRow
                      parentNodes={levels[idx]}
                      level={idx + 1}
                      filter={groupMode}
                    />
                  </div>
                ))} */}

              {levels.slice(1).map((_levelNodes, idx) => (
                <div key={`level-${idx + 1}`}>
                  <ConnectorRow parentNodes={levels[idx]} parentPath={[]} />
                  <ChildNodeRow
                    parentNodes={levels[idx]}
                    level={idx + 1}
                    filter={groupMode}
                    parentPath={[]}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {!loading && legendItems.length > 0 ? (
        <div className="rounded-radius border border-border/70 bg-muted/20 px-3 py-2 flex items-center justify">
          <div className="mt-2 flex flex-wrap gap-2 justify-center w-full">
            {legendItems.map((item) => (
              <Badge
                key={item.key}
                variant="outline"
                className="flex items-center gap-2 border-border bg-background px-2 py-1 text-xs text-foreground"
              >
                <span
                  aria-hidden="true"
                  className={`h-2.5 w-2.5 rounded-full ${item.colorClass}`}
                />
                <span>{item.label}</span>
              </Badge>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default OrganizationDiagram;
